import { API_URL } from '../services/apiConstants';

/**
 * Redux reducer for bulk violation resolution flow.
 *
 * Manages state for:
 * - Fetching items from queued emails
 * - Item selection (checkboxes)
 * - Submitting resolution requests
 * - Success/error states
 *
 * API calls are handled within thunk actions following the portfolio/property management pattern.
 */

// Action Types
const FETCH_ITEMS_REQUEST = 'RESOLVE_VIOLATIONS/FETCH_ITEMS_REQUEST';
const FETCH_ITEMS_SUCCESS = 'RESOLVE_VIOLATIONS/FETCH_ITEMS_SUCCESS';
const FETCH_ITEMS_FAILURE = 'RESOLVE_VIOLATIONS/FETCH_ITEMS_FAILURE';

const SUBMIT_RESOLUTION_REQUEST =
	'RESOLVE_VIOLATIONS/SUBMIT_RESOLUTION_REQUEST';
const SUBMIT_RESOLUTION_SUCCESS =
	'RESOLVE_VIOLATIONS/SUBMIT_RESOLUTION_SUCCESS';
const SUBMIT_RESOLUTION_FAILURE =
	'RESOLVE_VIOLATIONS/SUBMIT_RESOLUTION_FAILURE';

const TOGGLE_ITEM_SELECTION = 'RESOLVE_VIOLATIONS/TOGGLE_ITEM_SELECTION';
const TOGGLE_ALL_ITEMS = 'RESOLVE_VIOLATIONS/TOGGLE_ALL_ITEMS';
const RESET_STATE = 'RESOLVE_VIOLATIONS/RESET_STATE';

// Initial State
const initialState = {
	items: [],
	selectedItems: [],
	isLoading: false,
	isSubmitting: false,
	error: null,
	submitSuccess: false,
	submitMessage: null,
};

/**
 * Resolve violations reducer.
 *
 * @param {Object} state Current state
 * @param {Object} action Dispatched action
 * @returns {Object} New state
 */
export default function resolveViolationsReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_ITEMS_REQUEST:
			return {
				...state,
				isLoading: true,
				error: null,
			};

		case FETCH_ITEMS_SUCCESS:
			return {
				...state,
				isLoading: false,
				items: action.payload.items,
				error: null,
			};

		case FETCH_ITEMS_FAILURE:
			return {
				...state,
				isLoading: false,
				error: action.payload.error,
			};

		case TOGGLE_ITEM_SELECTION: {
			const itemId = action.payload.queuedEmailId;
			const isSelected = state.selectedItems.includes(itemId);

			return {
				...state,
				selectedItems: isSelected
					? state.selectedItems.filter((id) => id !== itemId)
					: [...state.selectedItems, itemId],
			};
		}

		case TOGGLE_ALL_ITEMS: {
			const openItemIds = state.items
				.filter((item) => item.is_open)
				.map((item) => item.queued_email_id);

			const allSelected = openItemIds.every((id) =>
				state.selectedItems.includes(id),
			);

			return {
				...state,
				selectedItems: allSelected ? [] : openItemIds,
			};
		}

		case SUBMIT_RESOLUTION_REQUEST:
			return {
				...state,
				isSubmitting: true,
				error: null,
				submitSuccess: false,
			};

		case SUBMIT_RESOLUTION_SUCCESS:
			return {
				...state,
				isSubmitting: false,
				submitSuccess: true,
				submitMessage: action.payload.message,
				selectedItems: [],
			};

		case SUBMIT_RESOLUTION_FAILURE:
			return {
				...state,
				isSubmitting: false,
				error: action.payload.error,
				submitSuccess: false,
			};

		case RESET_STATE:
			return initialState;

		default:
			return state;
	}
}

// Action Creators

/**
 * Request to fetch items for resolution.
 * @returns {Object} Action
 */
export const fetchItemsRequest = () => ({
	type: FETCH_ITEMS_REQUEST,
});

/**
 * Successfully fetched items.
 * @param {Array} items Array of items
 * @returns {Object} Action
 */
export const fetchItemsSuccess = (items) => ({
	type: FETCH_ITEMS_SUCCESS,
	payload: { items },
});

/**
 * Failed to fetch items.
 * @param {string} error Error message
 * @returns {Object} Action
 */
export const fetchItemsFailure = (error) => ({
	type: FETCH_ITEMS_FAILURE,
	payload: { error },
});

/**
 * Toggle selection of a single item.
 * @param {number} queuedEmailId The queued email ID
 * @returns {Object} Action
 */
export const toggleItemSelection = (queuedEmailId) => ({
	type: TOGGLE_ITEM_SELECTION,
	payload: { queuedEmailId },
});

/**
 * Toggle selection of all open items.
 * @returns {Object} Action
 */
export const toggleAllItems = () => ({
	type: TOGGLE_ALL_ITEMS,
});

/**
 * Request to submit resolution.
 * @returns {Object} Action
 */
export const submitResolutionRequest = () => ({
	type: SUBMIT_RESOLUTION_REQUEST,
});

/**
 * Successfully submitted resolution.
 * @param {string} message Success message
 * @returns {Object} Action
 */
export const submitResolutionSuccess = (message) => ({
	type: SUBMIT_RESOLUTION_SUCCESS,
	payload: { message },
});

/**
 * Failed to submit resolution.
 * @param {string} error Error message
 * @returns {Object} Action
 */
export const submitResolutionFailure = (error) => ({
	type: SUBMIT_RESOLUTION_FAILURE,
	payload: { error },
});

/**
 * Reset reducer state to initial.
 * @returns {Object} Action
 */
export const resetState = () => ({
	type: RESET_STATE,
});

// Thunk Actions

/**
 * Fetch items for bulk resolution.
 *
 * @param {number} userId User ID
 * @param {string} emailIds Comma-separated email IDs
 * @returns {Function} Thunk function
 */
export const fetchItems = (userId, emailIds) => {
	return async (dispatch) => {
		dispatch(fetchItemsRequest());

		try {
			const params = new URLSearchParams({
				user_id: userId,
				email_ids: emailIds,
			});

			const response = await fetch(
				`${API_URL}/api/resolve-violations/items?${params}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				},
			);

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || 'Failed to fetch items');
			}

			if (data.success && data.data) {
				dispatch(fetchItemsSuccess(data.data.items || []));
			} else {
				dispatch(fetchItemsFailure(data.message || 'Failed to fetch items'));
			}
		} catch (error) {
			console.error('fetchItems error:', error);
			dispatch(fetchItemsFailure(error.message || 'Network error occurred'));
		}
	};
};

/**
 * Submit resolution request.
 *
 * @param {number} userId User ID
 * @param {Array} items Array of items to resolve
 * @returns {Function} Thunk function
 */
export const submitResolution = (userId, items) => {
	return async (dispatch) => {
		dispatch(submitResolutionRequest());

		try {
			const response = await fetch(`${API_URL}/api/resolve-violations/submit`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					user_id: userId,
					items,
				}),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || 'Failed to submit resolution request');
			}

			if (data.success) {
				dispatch(
					submitResolutionSuccess(
						data.message || 'Resolution request submitted successfully',
					),
				);
			} else {
				dispatch(
					submitResolutionFailure(
						data.message || 'Failed to submit resolution request',
					),
				);
			}
		} catch (error) {
			console.error('submitResolution error:', error);
			dispatch(
				submitResolutionFailure(error.message || 'Network error occurred'),
			);
		}
	};
};
